import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Divider,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Campaign,
  Email,
  Delete,
  Add,
  Link as LinkIcon,
  Person,
  Schedule,
  Forum,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { sendBroadcast, getAnnouncementsByOrg } from '@services/announcementService';
import { getOrganizationById, updateOrganization } from '@services/organizationService';

// Icon map for common group platforms
const LINK_ICONS = {
  signal: '📡',
  groupme: '💬',
  discord: '🎮',
  whatsapp: '📱',
  slack: '💼',
  facebook: '📘',
  telegram: '✈️',
};

const getLinkIcon = (url = '', name = '') => {
  const combined = (url + name).toLowerCase();
  for (const [key, icon] of Object.entries(LINK_ICONS)) {
    if (combined.includes(key)) return icon;
  }
  return '🔗';
};

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const CommunicationsPage = () => {
  const navigate = useNavigate();
  const { userData, isOrgAdmin, activeOrganizationId } = useAuth();

  const organizationId = activeOrganizationId || userData?.organizationId;

  // Compose state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [snackbar, setSnackbar] = useState('');

  // History state
  const [announcements, setAnnouncements] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Community links state
  const [links, setLinks] = useState([]);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkDesc, setNewLinkDesc] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  useEffect(() => {
    if (!isOrgAdmin()) {
      navigate('/dashboard');
    }
  }, [isOrgAdmin, navigate]);

  const loadData = useCallback(async () => {
    if (!organizationId) return;
    try {
      setHistoryLoading(true);
      const [history, org] = await Promise.all([
        getAnnouncementsByOrg(organizationId),
        getOrganizationById(organizationId),
      ]);
      setAnnouncements(history);
      setLinks(org.communityLinks || []);
    } catch (err) {
      console.error('Error loading communications data:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      setComposeError('Title and message are required.');
      return;
    }
    try {
      setSending(true);
      setComposeError('');
      const result = await sendBroadcast({ title, message, organizationId, sendEmail });
      setTitle('');
      setMessage('');
      const emailNote = sendEmail ? ` · ${result.sent} email${result.sent !== 1 ? 's' : ''} sent` : '';
      setSnackbar(`Announcement posted${emailNote}.`);
      await loadData();
    } catch (err) {
      setComposeError(err?.message || 'Failed to send announcement. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLinkName.trim() || !newLinkUrl.trim()) return;
    try {
      setSavingLink(true);
      const updated = [
        ...links,
        {
          id: Date.now().toString(),
          name: newLinkName.trim(),
          url: newLinkUrl.trim(),
          description: newLinkDesc.trim(),
        },
      ];
      await updateOrganization(organizationId, { communityLinks: updated });
      setLinks(updated);
      setLinkDialogOpen(false);
      setNewLinkName('');
      setNewLinkUrl('');
      setNewLinkDesc('');
      setSnackbar('Link added.');
    } catch (err) {
      console.error('Error saving link:', err);
    } finally {
      setSavingLink(false);
    }
  };

  const handleDeleteLink = async (id) => {
    const updated = links.filter((l) => l.id !== id);
    await updateOrganization(organizationId, { communityLinks: updated });
    setLinks(updated);
    setSnackbar('Link removed.');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Campaign color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Communications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userData?.organizationName} — send announcements and manage community links
          </Typography>
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        {/* Left column: Compose + History */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Compose */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Forum color="primary" />
              <Typography variant="h6" fontWeight="bold">
                New Announcement
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Email fontSize="small" color={sendEmail ? 'primary' : 'disabled'} />
                    <Typography variant="body2">Also send by email to all members</Typography>
                  </Box>
                }
              />
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Campaign />}
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? 'Sending…' : 'Post Announcement'}
              </Button>
            </Box>

            {composeError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {composeError}
              </Alert>
            )}
          </Paper>

          {/* History */}
          <Paper elevation={2} sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Schedule color="action" fontSize="small" />
              <Typography variant="h6" fontWeight="bold">
                Announcement History
              </Typography>
              {announcements.length > 0 && (
                <Chip label={announcements.length} size="small" />
              )}
            </Box>

            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : announcements.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Campaign sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No announcements yet.</Typography>
              </Box>
            ) : (
              announcements.map((a, idx) => (
                <Box key={a.id}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>
                        <Person sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {a.title}
                          </Typography>
                          {a.sendEmail && (
                            <Chip
                              icon={<Email sx={{ fontSize: 12 }} />}
                              label={`${a.emailSent ?? 0} emailed`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {a.sentByName} · {formatDate(a.createdAt)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                          {a.message}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Box>

        {/* Right column: Community Links */}
        <Box sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0 }}>
          <Paper elevation={2} sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="action" fontSize="small" />
                <Typography variant="h6" fontWeight="bold">
                  Community Links
                </Typography>
              </Box>
              <Tooltip title="Add link">
                <IconButton size="small" color="primary" onClick={() => setLinkDialogOpen(true)}>
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>

            {links.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center', px: 2 }}>
                <LinkIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Add links to your Signal group, GroupMe, Discord, or any community space.
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  sx={{ mt: 2 }}
                  onClick={() => setLinkDialogOpen(true)}
                >
                  Add First Link
                </Button>
              </Box>
            ) : (
              <List dense disablePadding>
                {links.map((link, idx) => (
                  <Box key={link.id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      sx={{ py: 1.5, pr: 7 }}
                      component="a"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Box sx={{ mr: 1.5, fontSize: 22, lineHeight: 1 }}>
                        {getLinkIcon(link.url, link.name)}
                      </Box>
                      <ListItemText
                        primary={link.name}
                        secondary={link.description || link.url}
                        primaryTypographyProps={{ fontWeight: 'medium', variant: 'body2' }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          noWrap: true,
                          sx: { maxWidth: 220 },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => { e.preventDefault(); handleDeleteLink(link.id); }}
                          >
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Stack>

      {/* Add Link Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Community Link</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            placeholder="e.g. Signal Group, Discord Server"
            fullWidth
            value={newLinkName}
            onChange={(e) => setNewLinkName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="URL"
            placeholder="https://"
            fullWidth
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description (optional)"
            placeholder="Short description shown to volunteers"
            fullWidth
            value={newLinkDesc}
            onChange={(e) => setNewLinkDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddLink}
            disabled={savingLink || !newLinkName.trim() || !newLinkUrl.trim()}
            startIcon={savingLink ? <CircularProgress size={16} /> : <Add />}
          >
            Add Link
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default CommunicationsPage;
